import * as FC from "@fluentui/react-components";
import * as IC from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { EquipmentStatusBadge } from "../../components/equipment/equipment_status_badge";
import { ReminderSummaryCards } from "../../components/equipment/reminder_summary_cards";
import { SimpleGrid } from "../../components/simple_grid";
import { useDrawer } from "../../components/use_drawer";
import { toDateTimeString } from "../../date_utils";
import { useToast } from "../../hooks/useToast";
import { apiClient } from "../../services/api";
import {
    EQUIPMENT_DEFAULT_PAGE_SIZE,
    EQUIPMENT_DEFAULT_SORT_BY,
    EQUIPMENT_DEFAULT_SORT_DIRECTION,
    archiveEquipment,
    changeEquipmentStatus,
    equipmentMaintenanceStateOptions,
    equipmentOperationalStatusLabel,
    equipmentOperationalStatusOptions,
    equipmentSortOptions,
    equipmentTypeLabel,
    equipmentTypeOptions,
    equipmentWarrantyStateOptions,
    formatDateOnly,
    getEquipmentReminderSummary,
    listEquipment,
    resolveEquipmentPermissions,
    restoreEquipment,
} from "../../services/equipment_service";
import { getCurrentUser } from "../../services/login";
import { EquipmentForm } from "./equipment_form";

const useStyles = FC.makeStyles({
    root: {
        display: "flex",
        flexDirection: "column",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("10px"),
        overflow: "auto",
    },
    filters: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "end",
        ...FC.shorthands.gap("10px"),
        ...FC.shorthands.padding("12px"),
        backgroundColor: FC.tokens.colorNeutralBackground2,
        ...FC.shorthands.borderRadius(FC.tokens.borderRadiusMedium),
    },
    field: {
        display: "flex",
        minWidth: "150px",
        flexDirection: "column",
        ...FC.shorthands.gap("4px"),
    },
    actions: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        ...FC.shorthands.gap("8px"),
    },
    mono: {
        fontFamily: "Consolas, monospace",
    },
    muted: {
        color: FC.tokens.colorNeutralForeground3,
    },
});

const pageSizeOptions = [20, 50, 100];

const defaultQuery = {
    tenantId: "",
    keyword: "",
    type: "",
    operationalStatus: "",
    maintenanceState: "",
    warrantyState: "",
    custodianUserId: "",
    includeArchived: false,
    reminderScope: "",
    page: 1,
    pageSize: EQUIPMENT_DEFAULT_PAGE_SIZE,
    sortBy: EQUIPMENT_DEFAULT_SORT_BY,
    sortDirection: EQUIPMENT_DEFAULT_SORT_DIRECTION,
};

const selectedLabel = (options, value) =>
    options.find((option) => option.value === value)?.label || "";

const brandModel = (item) => [item.manufacturer, item.model].filter(Boolean).join(" ") || "-";

const errorText = (error) => {
    const trace = error?.traceId ? `（追踪号 ${error.traceId}）` : "";
    return `${error?.message || "操作失败"}${trace}`;
};

export const EquipmentManagement = () => {
    const styles = useStyles();
    const showToast = useToast();
    const nav = useNavigate();
    const permissions = useMemo(() => resolveEquipmentPermissions(getCurrentUser()), []);
    const { openDrawer, closeDrawer, drawer } = useDrawer({ style: { width: 720 } });

    const [query, setQuery] = useState(defaultQuery);
    const [keywordInput, setKeywordInput] = useState("");
    const [activeCard, setActiveCard] = useState("total");
    const [page, setPage] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [listError, setListError] = useState("");
    const [tenants, setTenants] = useState([]);
    const [users, setUsers] = useState([]);
    const [statusTarget, setStatusTarget] = useState(null);
    const [statusValue, setStatusValue] = useState("");
    const [statusReason, setStatusReason] = useState("");
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [archiveReason, setArchiveReason] = useState("");

    // Monotonic request id: only the newest in-flight list may commit, so a slow older
    // response can never overwrite a newer one.
    const requestRef = useRef(0);
    const summaryRequestRef = useRef(0);
    // Latest applied query, read by the stable `reload` callback below.
    const queryRef = useRef(query);

    const loadList = useCallback(async (activeQuery) => {
        const seq = ++requestRef.current;
        setLoading(true);
        setListError("");
        try {
            // Server-side filtering, sorting and paging only; rows are rendered as returned.
            const result = await listEquipment(activeQuery);
            if (seq === requestRef.current) {
                setPage(result);
            }
        } catch (error) {
            console.error("Failed to load equipment list:", error);
            if (seq === requestRef.current) {
                setPage(null);
                setListError(errorText(error));
            }
        } finally {
            if (seq === requestRef.current) {
                setLoading(false);
            }
        }
    }, []);

    // 与列表用同一套序号防护：Admin 快速切租户时，A 的迟到响应不能覆盖 B 的数字，
    // 否则卡片显示的是上一个租户的计数，而列表是当前租户的，两者对不上。
    const loadSummary = useCallback(async (tenantId) => {
        const seq = summaryRequestRef.current + 1;
        summaryRequestRef.current = seq;
        try {
            const result = await getEquipmentReminderSummary(tenantId || undefined);
            if (seq === summaryRequestRef.current) {
                setSummary(result);
            }
        } catch (error) {
            console.error("Failed to load equipment reminder summary:", error);
            if (seq === summaryRequestRef.current) {
                setSummary(null);
            }
        }
    }, []);

    useEffect(() => {
        queryRef.current = query;
        loadList(query);
    }, [loadList, query]);

    useEffect(() => {
        loadSummary(query.tenantId);
    }, [loadSummary, query.tenantId]);

    useEffect(() => {
        if (!permissions.isAdmin) {
            return;
        }
        apiClient
            .getTenants({ status: "active" })
            .then((items) => setTenants(items || []))
            .catch((error) => console.error("Failed to load tenants:", error));
    }, [permissions.isAdmin]);

    useEffect(() => {
        apiClient
            .getUsers()
            .then((items) => setUsers(items || []))
            .catch((error) => console.error("Failed to load users:", error));
    }, []);

    // Stable identity on purpose: the memoized column callbacks capture it once, so it must read
    // the current filters from the ref instead of closing over a stale `query`.
    const reload = useCallback(() => {
        loadList(queryRef.current);
        loadSummary(queryRef.current.tenantId);
    }, [loadList, loadSummary]);

    /**
     * Manual filter change. `reminderScope` is a reminder-card mechanism only, so it is dropped
     * here: no hidden predicate may survive a filter the user can see.
     */
    const patchQuery = (patch) => {
        setActiveCard(null);
        setQuery((previous) => ({ ...previous, reminderScope: "", ...patch, page: 1 }));
    };

    /**
     * Card click. Every other filter is reset so the list reproduces exactly the set the backend
     * counted for that card — including `reminderScope=actionable` for the three date cards.
     */
    const applyCardFilter = (key, filter) => {
        setActiveCard(key);
        setKeywordInput("");
        setQuery((previous) => ({
            ...defaultQuery,
            tenantId: previous.tenantId,
            pageSize: previous.pageSize,
            sortBy: previous.sortBy,
            sortDirection: previous.sortDirection,
            ...filter,
        }));
    };

    const applyKeyword = () => {
        patchQuery({ keyword: keywordInput.trim() });
    };

    const resetFilters = () => {
        setActiveCard("total");
        setKeywordInput("");
        setQuery((previous) => ({ ...defaultQuery, tenantId: previous.tenantId }));
    };

    const openForm = (item) => {
        openDrawer({
            header: item?.id ? "编辑器材" : "新增器材",
            body: (
                <EquipmentForm
                    item={item}
                    isAdmin={permissions.isAdmin}
                    onSaved={({ close }) => {
                        reload();
                        if (close) {
                            closeDrawer();
                        }
                    }}
                    onCancel={closeDrawer}
                />
            ),
        });
    };

    const runMutation = async (action, successMessage) => {
        try {
            await action();
            showToast(successMessage, "success");
            reload();
            return true;
        } catch (error) {
            console.error("Equipment mutation failed:", error);
            showToast("操作失败", "error", errorText(error));
            return false;
        }
    };

    const submitStatusChange = async () => {
        const target = statusTarget;
        if (!target || !statusValue) {
            return;
        }
        const done = await runMutation(
            () => changeEquipmentStatus(target.id, { operationalStatus: statusValue, reason: statusReason }),
            "运行状态已更新"
        );
        if (done) {
            setStatusTarget(null);
        }
    };

    const submitArchive = async () => {
        const target = archiveTarget;
        if (!target) {
            return;
        }
        const done = await runMutation(
            () =>
                target.isArchived
                    ? restoreEquipment(target.id, archiveReason)
                    : archiveEquipment(target.id, archiveReason),
            target.isArchived ? "器材已恢复" : "器材已归档"
        );
        if (done) {
            setArchiveTarget(null);
        }
    };

    const columns = useMemo(() => {
        const cells = [
            FC.createTableColumn({
                columnId: "assetCode",
                renderHeaderCell: () => "资产编号",
                renderCell: (item) => (
                    <FC.TableCellLayout>
                        <FC.Link className={styles.mono} onClick={() => nav(`/equipment/${item.id}`)}>
                            {item.assetCode}
                        </FC.Link>
                    </FC.TableCellLayout>
                ),
            }),
            FC.createTableColumn({
                columnId: "name",
                renderHeaderCell: () => "名称",
                renderCell: (item) => (
                    <FC.TableCellLayout>
                        {item.name}
                        {item.isArchived && (
                            <FC.Badge appearance="tint" color="subtle" size="small" style={{ marginLeft: 6 }}>
                                已归档
                            </FC.Badge>
                        )}
                    </FC.TableCellLayout>
                ),
            }),
            FC.createTableColumn({
                columnId: "type",
                renderHeaderCell: () => "类型",
                renderCell: (item) => <FC.TableCellLayout>{equipmentTypeLabel(item.type)}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "brandModel",
                renderHeaderCell: () => "品牌型号",
                renderCell: (item) => <FC.TableCellLayout>{brandModel(item)}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "serialNumber",
                renderHeaderCell: () => "序列号",
                renderCell: (item) => (
                    <FC.TableCellLayout className={styles.mono}>{item.serialNumber || "-"}</FC.TableCellLayout>
                ),
            }),
            FC.createTableColumn({
                columnId: "operationalStatus",
                renderHeaderCell: () => "运行状态",
                renderCell: (item) => (
                    <FC.TableCellLayout>
                        <EquipmentStatusBadge kind="operationalStatus" value={item.operationalStatus} />
                    </FC.TableCellLayout>
                ),
            }),
            FC.createTableColumn({
                columnId: "maintenanceState",
                renderHeaderCell: () => "保养状态",
                renderCell: (item) => (
                    <FC.TableCellLayout>
                        <EquipmentStatusBadge
                            kind="maintenanceState"
                            value={item.maintenanceState}
                            daysRemaining={item.maintenanceDaysRemaining}
                        />
                    </FC.TableCellLayout>
                ),
            }),
            FC.createTableColumn({
                columnId: "nextMaintenanceDate",
                renderHeaderCell: () => "下次保养",
                renderCell: (item) => (
                    <FC.TableCellLayout>{formatDateOnly(item.nextMaintenanceDate) || "-"}</FC.TableCellLayout>
                ),
            }),
            FC.createTableColumn({
                columnId: "warrantyEndDate",
                renderHeaderCell: () => "保修截止",
                renderCell: (item) => (
                    <FC.TableCellLayout>
                        {formatDateOnly(item.warrantyEndDate) || "-"}
                        <FC.Badge
                            appearance="tint"
                            color={
                                item.warrantyState === "expired"
                                    ? "danger"
                                    : item.warrantyState === "due_soon"
                                    ? "warning"
                                    : "subtle"
                            }
                            size="small"
                            style={{ marginLeft: 6 }}
                        >
                            {selectedLabel(equipmentWarrantyStateOptions, item.warrantyState)}
                        </FC.Badge>
                    </FC.TableCellLayout>
                ),
            }),
            FC.createTableColumn({
                columnId: "custodianName",
                renderHeaderCell: () => "负责人",
                renderCell: (item) => <FC.TableCellLayout>{item.custodianName || "-"}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "location",
                renderHeaderCell: () => "存放位置",
                renderCell: (item) => <FC.TableCellLayout>{item.location || "-"}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "updatedAtUtc",
                renderHeaderCell: () => "更新时间",
                renderCell: (item) => <FC.TableCellLayout>{toDateTimeString(item.updatedAtUtc)}</FC.TableCellLayout>,
            }),
            FC.createTableColumn({
                columnId: "ops",
                renderHeaderCell: () => "操作",
                renderCell: (item) => (
                    <FC.TableCellLayout>
                        <FC.Tooltip content="查看详情" relationship="label">
                            <FC.Button
                                appearance="subtle"
                                icon={<IC.MoreCircleFilled />}
                                onClick={() => nav(`/equipment/${item.id}`)}
                            />
                        </FC.Tooltip>
                        {permissions.canWrite && (
                            <FC.Tooltip content="编辑" relationship="label">
                                <FC.Button
                                    appearance="subtle"
                                    icon={<IC.EditRegular />}
                                    onClick={() => openForm(item)}
                                />
                            </FC.Tooltip>
                        )}
                        {permissions.canWrite && (
                            <FC.Menu>
                                <FC.MenuTrigger disableButtonEnhancement>
                                    <FC.Button appearance="subtle" icon={<IC.MoreVerticalRegular />} />
                                </FC.MenuTrigger>
                                <FC.MenuPopover>
                                    <FC.MenuList>
                                        {/* 没有任何可执行的目标状态时不摆这个入口——例如已报废器材，
                                            或非管理员面对只剩 Admin 专属出边的退役器材。 */}
                                        {(item.allowedStatusTargets || []).length > 0 && (
                                            <FC.MenuItem
                                                onClick={() => {
                                                    setStatusTarget(item);
                                                    setStatusValue("");
                                                    setStatusReason("");
                                                }}
                                            >
                                                变更运行状态
                                            </FC.MenuItem>
                                        )}
                                        {/* 归档只对已退役/报废的器材有意义，canArchive 由后端按状态判定下发；
                                            已归档的始终可以恢复。不加这个判断，Admin 对可用器材点"归档"必然 409。 */}
                                        {permissions.canAdmin && (item.isArchived || item.canArchive) && (
                                            <FC.MenuItem
                                                onClick={() => {
                                                    setArchiveTarget(item);
                                                    setArchiveReason("");
                                                }}
                                            >
                                                {item.isArchived ? "恢复归档" : "归档"}
                                            </FC.MenuItem>
                                        )}
                                    </FC.MenuList>
                                </FC.MenuPopover>
                            </FC.Menu>
                        )}
                    </FC.TableCellLayout>
                ),
            }),
        ];
        return cells;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nav, permissions, styles.mono]);

    const items = page?.items || [];
    const totalPages = page?.totalPages || 0;
    const currentPage = page?.page || query.page;

    return (
        <div className={styles.root}>
            <div className={styles.actions}>
                <FC.Title3>器材台账</FC.Title3>
                {permissions.canWrite && (
                    <FC.Button appearance="primary" icon={<IC.AddRegular />} onClick={() => openForm(null)}>
                        新增器材
                    </FC.Button>
                )}
            </div>

            <ReminderSummaryCards summary={summary} activeKey={activeCard} onSelect={applyCardFilter} />

            <div className={styles.filters}>
                <div className={styles.field} style={{ minWidth: 240 }}>
                    <FC.Label>关键字</FC.Label>
                    <FC.Input
                        contentBefore={<IC.SearchFilled />}
                        placeholder="资产编号、序列号、名称、品牌、型号、位置"
                        value={keywordInput}
                        onChange={(_, data) => setKeywordInput(data.value)}
                        onKeyDown={(evt) => {
                            if (evt.key === "Enter") {
                                applyKeyword();
                            }
                        }}
                    />
                </div>
                {permissions.isAdmin && (
                    <div className={styles.field}>
                        <FC.Label>租户</FC.Label>
                        <FC.Dropdown
                            selectedOptions={query.tenantId ? [query.tenantId] : [""]}
                            value={
                                tenants.find((tenant) => String(tenant.id) === query.tenantId)?.name ||
                                "全部租户"
                            }
                            onOptionSelect={(_, data) => patchQuery({ tenantId: data.optionValue || "" })}
                        >
                            <FC.Option value="">全部租户</FC.Option>
                            {tenants.map((tenant) => (
                                <FC.Option key={tenant.id} value={String(tenant.id)} text={tenant.name}>
                                    {`${tenant.seq} - ${tenant.name}`}
                                </FC.Option>
                            ))}
                        </FC.Dropdown>
                    </div>
                )}
                <div className={styles.field}>
                    <FC.Label>器材类型</FC.Label>
                    <FC.Dropdown
                        selectedOptions={query.type ? [query.type] : [""]}
                        value={selectedLabel(equipmentTypeOptions, query.type) || "全部类型"}
                        onOptionSelect={(_, data) => patchQuery({ type: data.optionValue || "" })}
                    >
                        <FC.Option value="">全部类型</FC.Option>
                        {equipmentTypeOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </div>
                <div className={styles.field}>
                    <FC.Label>运行状态</FC.Label>
                    <FC.Dropdown
                        selectedOptions={query.operationalStatus ? [query.operationalStatus] : [""]}
                        value={selectedLabel(equipmentOperationalStatusOptions, query.operationalStatus) || "全部状态"}
                        onOptionSelect={(_, data) => patchQuery({ operationalStatus: data.optionValue || "" })}
                    >
                        <FC.Option value="">全部状态</FC.Option>
                        {equipmentOperationalStatusOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </div>
                <div className={styles.field}>
                    <FC.Label>保养状态</FC.Label>
                    <FC.Dropdown
                        selectedOptions={query.maintenanceState ? [query.maintenanceState] : [""]}
                        value={selectedLabel(equipmentMaintenanceStateOptions, query.maintenanceState) || "全部"}
                        onOptionSelect={(_, data) => patchQuery({ maintenanceState: data.optionValue || "" })}
                    >
                        <FC.Option value="">全部</FC.Option>
                        {equipmentMaintenanceStateOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </div>
                <div className={styles.field}>
                    <FC.Label>保修状态</FC.Label>
                    <FC.Dropdown
                        selectedOptions={query.warrantyState ? [query.warrantyState] : [""]}
                        value={selectedLabel(equipmentWarrantyStateOptions, query.warrantyState) || "全部"}
                        onOptionSelect={(_, data) => patchQuery({ warrantyState: data.optionValue || "" })}
                    >
                        <FC.Option value="">全部</FC.Option>
                        {equipmentWarrantyStateOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </div>
                <div className={styles.field}>
                    <FC.Label>负责人</FC.Label>
                    <FC.Dropdown
                        selectedOptions={query.custodianUserId ? [query.custodianUserId] : [""]}
                        value={
                            users.find((user) => String(user.id) === query.custodianUserId)?.name || "全部负责人"
                        }
                        onOptionSelect={(_, data) => patchQuery({ custodianUserId: data.optionValue || "" })}
                    >
                        <FC.Option value="">全部负责人</FC.Option>
                        {users.map((user) => (
                            <FC.Option key={user.id} value={String(user.id)} text={user.name}>
                                {user.name}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </div>
                <div className={styles.field} style={{ minWidth: 120 }}>
                    <FC.Label>排序字段</FC.Label>
                    <FC.Dropdown
                        selectedOptions={[query.sortBy]}
                        value={selectedLabel(equipmentSortOptions, query.sortBy)}
                        onOptionSelect={(_, data) =>
                            setQuery((previous) => ({ ...previous, sortBy: data.optionValue, page: 1 }))
                        }
                    >
                        {equipmentSortOptions.map((option) => (
                            <FC.Option key={option.value} value={option.value}>
                                {option.label}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                </div>
                <FC.Button
                    icon={query.sortDirection === "asc" ? <IC.ArrowSortUpRegular /> : <IC.ArrowSortDownRegular />}
                    onClick={() =>
                        setQuery((previous) => ({
                            ...previous,
                            sortDirection: previous.sortDirection === "asc" ? "desc" : "asc",
                            page: 1,
                        }))
                    }
                >
                    {query.sortDirection === "asc" ? "升序" : "降序"}
                </FC.Button>
                <FC.Checkbox
                    label="包含归档"
                    checked={query.includeArchived}
                    onChange={(_, data) => patchQuery({ includeArchived: Boolean(data.checked) })}
                />
                <FC.Button appearance="primary" disabled={loading} onClick={applyKeyword}>
                    {loading ? "查询中..." : "查询"}
                </FC.Button>
                <FC.Button onClick={resetFilters}>重置</FC.Button>
            </div>

            {listError && (
                <FC.MessageBar intent="error">
                    <FC.MessageBarBody>{listError}</FC.MessageBarBody>
                </FC.MessageBar>
            )}

            <SimpleGrid items={items} columns={columns} />
            {!loading && items.length === 0 && !listError && (
                <div className={styles.muted}>没有符合条件的器材</div>
            )}

            <div className={styles.actions}>
                <span className={styles.muted}>
                    {`共 ${page?.totalCount || 0} 条，第 ${currentPage} / ${Math.max(totalPages, 1)} 页`}
                </span>
                <div className={styles.actions}>
                    <FC.Label>每页</FC.Label>
                    <FC.Dropdown
                        style={{ minWidth: 90 }}
                        selectedOptions={[String(query.pageSize)]}
                        value={String(query.pageSize)}
                        onOptionSelect={(_, data) =>
                            setQuery((previous) => ({ ...previous, pageSize: Number(data.optionValue), page: 1 }))
                        }
                    >
                        {pageSizeOptions.map((size) => (
                            <FC.Option key={size} value={String(size)}>
                                {String(size)}
                            </FC.Option>
                        ))}
                    </FC.Dropdown>
                    <FC.Button
                        disabled={loading || currentPage <= 1}
                        onClick={() => setQuery((previous) => ({ ...previous, page: currentPage - 1 }))}
                    >
                        上一页
                    </FC.Button>
                    <FC.Button
                        disabled={loading || currentPage >= totalPages}
                        onClick={() => setQuery((previous) => ({ ...previous, page: currentPage + 1 }))}
                    >
                        下一页
                    </FC.Button>
                </div>
            </div>

            <FC.Dialog
                open={Boolean(statusTarget)}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setStatusTarget(null);
                    }
                }}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>变更运行状态</FC.DialogTitle>
                        <FC.DialogContent>
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                <div>
                                    {`${statusTarget?.assetCode || ""} ${statusTarget?.name || ""}，当前状态：${equipmentOperationalStatusLabel(
                                        statusTarget?.operationalStatus
                                    )}`}
                                </div>
                                <FC.Field label="目标状态" required>
                                    <FC.Dropdown
                                        selectedOptions={statusValue ? [statusValue] : []}
                                        value={equipmentOperationalStatusLabel(statusValue)}
                                        placeholder="选择目标状态"
                                        onOptionSelect={(_, data) => setStatusValue(data.optionValue)}
                                    >
                                        {/*
                                          * 直接渲染后端下发的 allowedStatusTargets：它已按状态机出边、
                                          * 人工可设目标和当前角色权限过滤过。前端不再自己维护一份转换表，
                                          * 否则副本漂移的症状是"菜单里每个选项提交都 409"。
                                          */}
                                        {(statusTarget?.allowedStatusTargets || [])
                                            .map((value) => ({
                                                value,
                                                label: equipmentOperationalStatusLabel(value),
                                            }))
                                            .map((option) => (
                                                <FC.Option key={option.value} value={option.value}>
                                                    {option.label}
                                                </FC.Option>
                                            ))}
                                    </FC.Dropdown>
                                </FC.Field>
                                <FC.Field label="原因" hint="退役、报废和恢复可用必须填写原因">
                                    <FC.Textarea
                                        rows={2}
                                        value={statusReason}
                                        onChange={(_, data) => setStatusReason(data.value)}
                                    />
                                </FC.Field>
                            </div>
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setStatusTarget(null)}>
                                取消
                            </FC.Button>
                            <FC.Button appearance="primary" disabled={!statusValue} onClick={submitStatusChange}>
                                提交
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>

            <FC.Dialog
                open={Boolean(archiveTarget)}
                onOpenChange={(_, data) => {
                    if (!data.open) {
                        setArchiveTarget(null);
                    }
                }}
            >
                <FC.DialogSurface>
                    <FC.DialogBody>
                        <FC.DialogTitle>{archiveTarget?.isArchived ? "恢复归档" : "归档器材"}</FC.DialogTitle>
                        <FC.DialogContent>
                            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                                <div>
                                    {archiveTarget?.isArchived
                                        ? "恢复归档只清除归档标记，不改变运行状态。"
                                        : "只有退役或报废的器材可以归档；归档后默认列表不再显示。"}
                                </div>
                                <FC.Field label="原因">
                                    <FC.Textarea
                                        rows={2}
                                        value={archiveReason}
                                        onChange={(_, data) => setArchiveReason(data.value)}
                                    />
                                </FC.Field>
                            </div>
                        </FC.DialogContent>
                        <FC.DialogActions>
                            <FC.Button appearance="secondary" onClick={() => setArchiveTarget(null)}>
                                取消
                            </FC.Button>
                            <FC.Button appearance="primary" onClick={submitArchive}>
                                确认
                            </FC.Button>
                        </FC.DialogActions>
                    </FC.DialogBody>
                </FC.DialogSurface>
            </FC.Dialog>

            {drawer}
        </div>
    );
};
